import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { Fragment, createElement } from 'react';
import { isString, isEmptyArray, object } from '@remirror/core';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import { jsx, jsxs } from 'react/jsx-runtime';

var Callout = props => {
  var _props$node$attrs;

  var content = props.node.content;

  if (!content) {
    return null;
  }

  var children = content.map((node, ii) => {
    return /*#__PURE__*/jsx(RemirrorRenderer, _objectSpread({
      json: node
    }, props), ii);
  });

  var _ref = (_props$node$attrs = props.node.attrs) !== null && _props$node$attrs !== void 0 ? _props$node$attrs : {},
      type = _ref.type,
      emoji = _ref.emoji;

  var dataAttrs = {
    'data-callout-type': type
  };

  if (emoji) {
    dataAttrs['data-callout-emoji'] = emoji;
  }

  return /*#__PURE__*/jsxs("div", _objectSpread(_objectSpread({}, dataAttrs), {}, {
    children: [emoji ? /*#__PURE__*/jsx("div", {
      className: "remirror-callout-emoji-wrapper",
      children: /*#__PURE__*/jsx("span", {
        children: emoji
      })
    }) : null, /*#__PURE__*/jsx("div", {
      children: children
    })]
  }));
};

var _excluded$2 = ["node"];

var normalizeMark = mark => isString(mark) ? {
  type: mark,
  attrs: {}
} : _objectSpread({
  attrs: {}
}, mark);

var TextHandler = _ref => {
  var node = _ref.node,
      props = _objectWithoutProperties(_ref, _excluded$2);

  if (!node.text) {
    return null;
  }

  var textElement = /*#__PURE__*/jsx(Fragment, {
    children: node.text
  });

  if (!node.marks) {
    return textElement;
  }

  var _iterator = _createForOfIteratorHelper(node.marks),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var mark = _step.value;
      var normalized = normalizeMark(mark);
      var MarkHandler = props.markMap[normalized.type];

      if (!MarkHandler) {
        if (!props.skipUnknownMarks) {
          throw new Error("No handler for mark type `".concat(normalized.type, "` registered"));
        }

        continue;
      }

      textElement = /*#__PURE__*/jsx(MarkHandler, _objectSpread(_objectSpread({}, normalized.attrs), {}, {
        children: textElement
      }));
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  return textElement;
};

var CodeBlock = props => {
  var content = props.node.content;

  if (!content) {
    return null;
  }

  var children = content.map((node, ii) => {
    return /*#__PURE__*/jsx(TextHandler, _objectSpread({}, _objectSpread(_objectSpread({}, props), {}, {
      node
    })), ii);
  });
  return /*#__PURE__*/jsx("pre", {
    children: /*#__PURE__*/jsx("code", {
      children: children
    })
  });
};

var Heading = props => {
  var _ref, _props$node$attrs;

  var content = props.node.content;

  if (!content) {
    return null;
  }

  var children = content.map((node, ii) => {
    return /*#__PURE__*/jsx(TextHandler, _objectSpread({}, _objectSpread(_objectSpread({}, props), {}, {
      node
    })), ii);
  });
  var level = (_ref = (_props$node$attrs = props.node.attrs) === null || _props$node$attrs === void 0 ? void 0 : _props$node$attrs.level) !== null && _ref !== void 0 ? _ref : 1;
  return /*#__PURE__*/createElement("h".concat(level), null, children);
};

var _excluded$1 = ["allowFullScreen", "type"];
var createIFrameHandler = overwriteAttrs => {
  var iframeHandler = props => {
    var _props$node$attrs;

    // Remove remirror-internal attributes
    var _ref = (_props$node$attrs = props.node.attrs) !== null && _props$node$attrs !== void 0 ? _props$node$attrs : {},
        allowFullScreen = _ref.allowFullScreen;
        _ref.type;
        var attrs = _objectWithoutProperties(_ref, _excluded$1);

    var normalizedAttrs = _objectSpread(_objectSpread({}, attrs), {}, {
      // Prevent React error that allowFullScreen must be boolean (but is string in remirror)
      allowFullScreen: allowFullScreen !== 'false'
    }, overwriteAttrs);

    return /*#__PURE__*/jsx("iframe", _objectSpread({}, normalizedAttrs));
  };

  return iframeHandler;
};

var createLinkHandler = overwriteAttrs => {
  var linkHandler = _ref => {
    var href = _ref.href,
        target = _ref.target,
        children = _ref.children;

    var normalizedAttrs = _objectSpread({
      href,
      target: target !== null && target !== void 0 ? target : undefined
    }, overwriteAttrs);

    return /*#__PURE__*/jsx("a", _objectSpread(_objectSpread({}, normalizedAttrs), {}, {
      children: children
    }));
  };

  return linkHandler;
};

var _excluded = ["node"];
var Doc = _ref => {
  var _node$attrs;

  var node = _ref.node,
      props = _objectWithoutProperties(_ref, _excluded);

  var content = node.content;

  if (!content || isEmptyArray(content)) {
    return null;
  }

  var children = content.map((child, ii) => {
    return /*#__PURE__*/createElement(RemirrorRenderer, _objectSpread(_objectSpread({
      json: child
    }, props), {}, {
      key: ii
    }));
  });
  return /*#__PURE__*/jsx("div", _objectSpread(_objectSpread({}, (_node$attrs = node.attrs) !== null && _node$attrs !== void 0 ? _node$attrs : object()), {}, {
    children: children
  }));
};
var defaultTypeMap = {
  blockquote: 'blockquote',
  bulletList: 'ul',
  callout: Callout,
  doc: Doc,
  heading: Heading,
  paragraph: 'p',
  horizontalRule: 'hr',
  iframe: createIFrameHandler(),
  image: 'img',
  hardBreak: 'br',
  codeBlock: CodeBlock,
  orderedList: 'ol',
  text: TextHandler
};
var defaultMarkMap = {
  italic: 'em',
  bold: 'strong',
  code: 'code',
  link: createLinkHandler(),
  underline: 'u'
};

/**
 * A recursively rendered tree.
 */
var RemirrorRenderer = props => {
  var _json$attrs;

  var json = props.json,
      _props$markMap = props.markMap,
      markMap = _props$markMap === void 0 ? defaultMarkMap : _props$markMap,
      _props$skipUnknownMar = props.skipUnknownMarks,
      skipUnknownMarks = _props$skipUnknownMar === void 0 ? false : _props$skipUnknownMar,
      _props$skipUnknownTyp = props.skipUnknownTypes,
      skipUnknownTypes = _props$skipUnknownTyp === void 0 ? false : _props$skipUnknownTyp,
      _props$typeMap = props.typeMap,
      typeMap = _props$typeMap === void 0 ? defaultTypeMap : _props$typeMap;

  if (json.type === 'text' && json.text && (!json.marks || isEmptyArray(json.marks))) {
    return /*#__PURE__*/jsx(Fragment, {
      children: json.text
    }); // For some reason FunctionalComponent don't allow returning react-nodes
  }

  var rest = {
    markMap,
    typeMap,
    skipUnknownMarks,
    skipUnknownTypes
  };
  var TypeHandler = typeMap[json.type];

  if (!TypeHandler) {
    if (!skipUnknownTypes) {
      throw new Error("No handler for node type `".concat(json.type, "` registered"));
    }

    return null;
  }

  var childProps = isString(TypeHandler) ? (_json$attrs = json.attrs) !== null && _json$attrs !== void 0 ? _json$attrs : object() : _objectSpread(_objectSpread({}, rest), {}, {
    node: json
  });
  var content = json.content;

  if (!content || isEmptyArray(content)) {
    return /*#__PURE__*/jsx(TypeHandler, _objectSpread({}, childProps));
  }

  var children = content.map((child, ii) => {
    return /*#__PURE__*/jsx(RemirrorRenderer, _objectSpread({
      json: child
    }, rest), ii);
  });
  return /*#__PURE__*/jsx(TypeHandler, _objectSpread(_objectSpread({}, childProps), {}, {
    children: children
  }));
};

export { Callout, CodeBlock, Doc, Heading, RemirrorRenderer, TextHandler, createIFrameHandler, createLinkHandler };
